/**
 * Quais salas acabaram de ser atribuídas automaticamente ao operador.
 *
 * ## O defeito que isto corrige
 *
 * O observador de chats atribuídos esperava **4 segundos** depois de montar
 * (no login) para marcar como "já conhecidas" as salas em andamento — a ideia
 * era não piscar o que já estava aberto.
 *
 * Mas as salas em andamento só carregam quando o operador entra na tela de
 * chat, normalmente muito depois desses 4 segundos. Quando o cronômetro
 * disparava, a lista de conhecidas ficava vazia; quando as salas chegavam,
 * TODAS contavam como recém-atribuídas — entravam no QuickAccess marcadas como
 * novas e o som tocava. Era o relato de 12/10/2026: entrar na tela de chat
 * abria todas as salas disponíveis.
 *
 * ## A correção
 *
 * O marco deixa de ser tempo e passa a ser **a primeira carga real da lista**.
 * O que vem nela já estava em andamento, então não pisca. O que aparece depois
 * é atribuição de verdade.
 *
 * Primeira carga VAZIA também conta: o operador que entra sem sala nenhuma e
 * recebe uma atribuição em seguida precisa ver a aba piscar.
 */

/** Os ids de uma lista de salas, como texto — o id vem ora número, ora string. */
export const idsDeSala = (salas) =>
    (Array.isArray(salas) ? salas : [])
        .filter((s) => s && s.room_id !== null && s.room_id !== undefined)
        .map((s) => String(s.room_id));

/**
 * @param {object} entrada
 * @param {Array} entrada.emAndamento salas em andamento do operador
 * @param {Set<string>} entrada.jaConhecidas ids que não devem piscar
 * @param {Array} entrada.noQuickAccess salas que já têm aba aberta
 * @param {boolean} entrada.primeiraCargaJaOcorreu false até a lista chegar uma vez
 * @returns {{novas: Array, aConhecer: string[]}} `novas` já marcadas com `__isNew`
 */
export const salasRecemAtribuidas = ({
    emAndamento,
    jaConhecidas,
    noQuickAccess,
    primeiraCargaJaOcorreu,
}) => {
    const lista = Array.isArray(emAndamento) ? emAndamento : [];
    const conhecidas = jaConhecidas instanceof Set ? jaConhecidas : new Set();

    const inéditas = lista.filter(
        (s) => s && s.room_id !== null && s.room_id !== undefined && !conhecidas.has(String(s.room_id))
    );

    // Toda sala avaliada passa a ser conhecida, inclusive as que não piscam --
    // senão ela seria reavaliada a cada atualização da lista.
    const aConhecer = idsDeSala(inéditas);

    // A primeira carga é o retrato do que já estava em andamento: nada pisca.
    if (!primeiraCargaJaOcorreu) {
        return { novas: [], aConhecer };
    }

    const jaAbertas = new Set(idsDeSala(noQuickAccess));

    const novas = inéditas
        .filter((s) => !jaAbertas.has(String(s.room_id)))
        .map((s) => ({ ...s, __isNew: true }));

    return { novas, aConhecer };
};
